import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RecommendationQuiz = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
  {
    id: 'homeType',
    question: 'What type of home do you have?',
    icon: 'Home',
    options: [
    { value: 'apartment', label: 'Apartment/Condo', description: 'Under 1,000 sq ft' },
    { value: 'house-small', label: 'Small House', description: '1,000-2,000 sq ft' },
    { value: 'house-large', label: 'Large House', description: 'Over 2,000 sq ft' },
    { value: 'multi-level', label: 'Multi-Level Home', description: 'Stairs involved' }]

  },
  {
    id: 'floorType',
    question: 'What flooring do you have most?',
    icon: 'Grid3X3',
    options: [
    { value: 'carpet', label: 'Mostly Carpet', description: 'Deep pile or low pile' },
    { value: 'hardwood', label: 'Hardwood/Laminate', description: 'Smooth surfaces' },
    { value: 'mixed', label: 'Mixed Flooring', description: 'Carpet and hard floors' },
    { value: 'tile', label: 'Tile/Stone', description: 'Kitchen and bathrooms' }]

  },
  {
    id: 'pets',
    question: 'Do you have pets?',
    icon: 'Heart',
    options: [
    { value: 'none', label: 'No Pets', description: 'Pet-free household' },
    { value: 'cats', label: 'Cats Only', description: 'Litter and light shedding' },
    { value: 'dogs', label: 'Dogs Only', description: 'Heavy shedding possible' },
    { value: 'multiple', label: 'Multiple Pets', description: 'Cats and dogs' }]

  },
  {
    id: 'budget',
    question: 'What\'s your budget range?',
    icon: 'DollarSign',
    options: [
    { value: 'budget', label: 'Under $200', description: 'Budget-friendly options' },
    { value: 'mid', label: '$200 - $500', description: 'Mid-range quality' },
    { value: 'premium', label: '$500 - $800', description: 'Premium features' },
    { value: 'luxury', label: '$800+', description: 'Top-of-the-line models' }]

  }];


  const handleAnswer = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));

    if (currentStep < questions?.length - 1) {
      setCurrentStep((prev) => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const getRecommendation = () => {
    const { homeType, floorType, pets, budget } = answers;

    // Simple recommendation logic
    if (pets === 'multiple' && budget === 'premium') {
      return {
        title: 'Dyson V15 Detect',
        reason: 'Perfect for multi-pet homes with advanced filtration',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e4d98958-1768061388011.png",
        alt: 'Modern cordless vacuum cleaner with digital display',
        price: '$749',
        features: ['Laser dust detection', 'Anti-tangle brush', 'HEPA filtration']
      };
    } else if (floorType === 'hardwood' && budget === 'budget') {
      return {
        title: 'Bissell Crosswave',
        reason: 'Excellent for hard floors on a budget',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1debce883-1767047397189.png",
        alt: 'Multi-surface cleaning machine on kitchen tile',
        price: '$179',
        features: ['Wet & dry cleaning', 'Multi-surface', 'Easy maintenance']
      };
    } else {
      return {
        title: 'Shark Navigator Lift-Away',
        reason: 'Versatile choice for most home types',
        image: "https://img.rocket.new/generatedImages/rocket_gen_img_1d4b4b0ce-1766875571363.png",
        alt: 'Blue upright vacuum with detachable canister',
        price: '$199',
        features: ['2-in-1 design', 'HEPA filter', 'Lightweight']
      };
    }
  };

  const resetQuiz = () => {
    setCurrentStep(0);
    setAnswers({});
    setShowResults(false);
  };

  if (showResults) {
    const recommendation = getRecommendation();

    return (
      <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <Icon name="CheckCircle" size={48} className="text-green-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Your Perfect Match!</h2>
            <p className="text-gray-600">Based on your answers, here's our top recommendation:</p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 items-center">
              <div className="aspect-square rounded-lg overflow-hidden">
                <img
                  src={recommendation?.image}
                  alt={recommendation?.alt}
                  className="w-full h-full object-cover" />

              </div>
              
              <div className="space-y-4">
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {recommendation?.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{recommendation?.reason}</p>
                  <div className="text-3xl font-bold text-primary mb-4">
                    {recommendation?.price}
                  </div>
                </div>

                <div className="space-y-2">
                  <h4 className="font-semibold text-gray-900">Key Features:</h4>
                  <ul className="space-y-1">
                    {recommendation?.features?.map((feature, index) =>
                    <li key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                        <Icon name="Check" size={16} className="text-green-500" />
                        <span>{feature}</span>
                      </li>
                    )}
                  </ul>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <Button
                    variant="default"
                    size="sm"
                    iconName="ExternalLink"
                    iconPosition="right"
                    asChild>

                    <Link to="/product-review">View Full Review</Link>
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="RotateCcw"
                    iconPosition="left"
                    onClick={resetQuiz}>

                    Retake Quiz
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>);

  }

  const currentQuestion = questions?.[currentStep];
  const progress = (currentStep + 1) / questions?.length * 100;

  return (
    <section className="bg-gradient-to-br from-blue-50 to-indigo-100 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Find Your Perfect Vacuum</h2>
          <p className="text-gray-600 mb-6">Answer 4 quick questions to get personalized recommendations</p>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-8">
            <div
              className="bg-primary h-2 rounded-full transition-all duration-500"
              style={{ width: `${progress}%` }}>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <Icon name={currentQuestion?.icon} size={32} className="text-primary" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2">
              {currentQuestion?.question}
            </h3>
            <p className="text-sm text-gray-500">
              Question {currentStep + 1} of {questions?.length}
            </p>
          </div>

          <div className="grid gap-3">
            {currentQuestion?.options?.map((option) =>
            <button
              key={option?.value}
              onClick={() => handleAnswer(currentQuestion?.id, option?.value)}
              className="p-4 border-2 border-gray-200 rounded-lg text-left hover:border-primary hover:bg-primary/5 transition-all duration-200 group">

                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900 group-hover:text-primary">
                      {option?.label}
                    </div>
                    <div className="text-sm text-gray-500 mt-1">
                      {option?.description}
                    </div>
                  </div>
                  <Icon
                  name="ChevronRight"
                  size={20}
                  className="text-gray-400 group-hover:text-primary" />

                </div>
              </button>
            )}
          </div>

          {currentStep > 0 &&
          <div className="mt-6 text-center">
              <Button
              variant="ghost"
              size="sm"
              iconName="ArrowLeft"
              iconPosition="left"
              onClick={() => setCurrentStep((prev) => prev - 1)}>

                Previous Question
              </Button>
            </div>
          }
        </div>
      </div>
    </section>);

};

export default RecommendationQuiz;